// This is a component that renders a list of given datasets it changes when user picks a different point it also sets the selected dataset.
import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select } from 'antd';
import styled from 'styled-components';

import { getOrMakeUnitDefinitionFromMdsSiMarkup } from '@metservicedev/cf-units';
import { useMDSContext } from '@metservice/use-mds';

import { sanitize } from '@mono/fuzzy-search';
import { datasetToPoint } from 'obs-mapbox-config';
import { datasourceDataset } from '@mono/types/src/mds';
import { useSpaceConfiguratorContext } from 'space-configurator-context';
import { toHash } from '@mono/to-hash';

interface SelectProps {
    obsList?: datasourceDataset[];
}

export const ObsSelect = ({ obsList }: SelectProps) => {
    const { findCapabilitiesByDataset, capabilities } = useMDSContext();
    const [selectableCapabilities, setSelectableCapabilities] = useState<string[]>([]);
    const { state, enableObs, setState, enableGriddedDataset } = useSpaceConfiguratorContext();

    const griddedUnit = useMemo(() => {
        if (!state.griddedCapabilityId) return;

        const forecastCapability = capabilities.find((c) => c.id === state.griddedCapabilityId);
        if (!forecastCapability) return;

        const variable = Object.values(forecastCapability.variables)[0];
        if (!variable) return;

        return getOrMakeUnitDefinitionFromMdsSiMarkup(variable.units).unit;
    }, [state.griddedCapabilityId, capabilities.map((c) => c.id).join()]);

    // When a new obs is selected, or the compatible units changes
    // We should get the capabilities for the obs and filter them by the units
    useEffect(() => {
        if (!state.obsId) {
            setSelectableCapabilities([]);
            return;
        }

        const abortController = new AbortController();

        const loadCapabilitiesForObs = async () => {
            if (!state.obsId) return;
            const { capabilities } = await findCapabilitiesByDataset(state.obsId, abortController.signal);

            setSelectableCapabilities(
                capabilities
                    .filter((c) => {
                        const variable = Object.values(c.variables)[0];
                        if (!variable) return false;

                        const siUnit = getOrMakeUnitDefinitionFromMdsSiMarkup(variable.units);
                        if (!siUnit) return false;

                        if (!griddedUnit) return true;

                        return griddedUnit === siUnit.unit;
                    })
                    .map((c) => c.id)
            );
        };
        loadCapabilitiesForObs();

        return () => {
            abortController.abort();
        };
    }, [state.obsId, griddedUnit]);

    const disabled = !enableObs || (enableGriddedDataset && !state.griddedCapabilityId);
    const showCapabilitySelector = useMemo(() => {
        if (!enableObs) return false;
        if (enableObs === true) return true;
        if (enableObs.enableCapability === true) return true;

        return false;
    }, [toHash(enableObs)]);

    return (
        <div style={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                <Input disabled value={'Obs'} style={{ width: 71, color: 'rgba(0, 0, 0, 0.85)', cursor: 'auto', marginRight: -1, textAlign: 'center' }} />
                <StyledSelect
                    allowClear
                    showSearch
                    style={{ flexGrow: 1 }}
                    value={state.obsId}
                    disabled={disabled}
                    placeholder={state.griddedCapabilityId ? 'Select an obs station' : ''}
                    filterOption={(input, option) => sanitize(option?.label).includes(sanitize(input))}
                    onClear={() => {
                        setState((prev) => ({ ...prev, obsId: undefined, obsCapabilityId: undefined }));
                    }}
                    onChange={(id: string) => {
                        const obs = obsList?.find((obs) => obs.id === id);
                        if (!obs) {
                            setState((prev) => ({ ...prev, obsId: undefined, obsCapabilityId: undefined }));
                            console.warn('this shouldnt happen. If this is happening, our point-obs have non-point obs datasets.');
                            return;
                        }

                        const coordinates = datasetToPoint(obs);
                        if (!coordinates) {
                            setState((prev) => ({ ...prev, obsId: undefined, obsCapabilityId: undefined }));
                            console.warn('this shouldnt happen. If this is happening, our point-obs have non-point obs datasets.');
                            return;
                        }

                        const space = { type: 'Point', coordinates } as const;
                        setState((prev) => ({ ...prev, obsId: obs.id, obsCapabilityId: undefined, space }));
                    }}
                    options={obsList?.map((dataset) => {
                        return { label: dataset.display_name_short, value: dataset.id };
                    })}
                />
            </div>
            {showCapabilitySelector && (
                <div style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                    <Input disabled value={'Type'} style={{ width: 71, color: 'rgba(0, 0, 0, 0.85)', cursor: 'auto', marginRight: -1, textAlign: 'center' }} />
                    <StyledSelect
                        allowClear
                        showSearch
                        disabled={!state.obsId || !enableObs}
                        style={{ flexGrow: 1 }}
                        value={state.obsCapabilityId}
                        placeholder={state.obsId ? 'Select a capability' : ''}
                        filterOption={(input, option) => sanitize(option?.label).includes(sanitize(input))}
                        onClear={() => {
                            setState((prev) => ({ ...prev, obsCapabilityId: undefined }));
                        }}
                        onChange={(capabilityId: string) => {
                            const obs = obsList?.find((obs) => obs.id === state.obsId);
                            if (!obs) {
                                console.warn('this shouldnt happen. If this is happening, our point-obs have non-point obs datasets.');
                                setState((prev) => ({ ...prev, obsCapabilityId: undefined }));
                                return;
                            }

                            const coordinates = datasetToPoint(obs);
                            if (!coordinates) {
                                setState((prev) => ({ ...prev, obsCapabilityId: undefined }));
                                console.warn('this shouldnt happen. If this is happening, our point-obs have non-point obs datasets.');
                                return;
                            }

                            setState((prev) => ({ ...prev, obsCapabilityId: capabilityId }));
                        }}
                        options={selectableCapabilities.map((capabilityId) => {
                            const capability = capabilities.find((c) => c.id === capabilityId);
                            if (!capability) return { label: 'unknown', value: capabilityId };

                            return { label: capability.display_name_short, value: capability.id };
                        })}
                    />
                </div>
            )}
        </div>
    );
};
const StyledSelect = styled(Select)`
    height: 32px;
    span {
        color: black !important;
    }
`;
